/* eslint-disable @typescript-eslint/quotes */
import {
    ButtonsOptions,
    ColorOptions,
    PaddingBottomOptions,
    PaddingLeftOptions,
    PaddingRightOptions,
    PaddingTopOptions,
    UndefinedOption
} from '@df/ui/atomic/atomic-options';
import { NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { CUSTOMER_GROUPS, customerGroupVisibilityCmsField } from '../../../customer/groups/customer-groups';
import { NPageHeader } from './page-header.interface';

export const PAGE_HEADER_VERSION_STATE: NPageHeader.IVersionState = {
    transparent: false,
    lightMode: false,
    hideUp: false
};

export const PAGE_HEADER_VERSION_CONFIG = [
    <NICmsContentField.IField>{
        key: 'section-header-version',
        type: NICmsContentField.EFieldType.label,
        title: 'Header Version'
    },
    <NICmsContentField.IToggleField>{
        key: 'headerTransparent',
        type: NICmsContentField.EFieldType.toggle,
        title: 'Transparent',
        labels: {
            true: 'Yes',
            false: 'No'
        },
        default: false
    },
    <NICmsContentField.IToggleField>{
        key: 'headerLightMode',
        type: NICmsContentField.EFieldType.toggle,
        title: 'Light Mode',
        labels: {
            true: 'Yes',
            false: 'No'
        },
        default: false,
        canShow: (data: NPageHeader.IBlockDataContent) => data?.headerTransparent || data?.typeData?.headerTransparent
    },
    <NICmsContentField.IToggleField>{
        key: 'headerHideUp',
        type: NICmsContentField.EFieldType.toggle,
        title: 'Hide Up',
        description: 'Hide up when start scrolling down',
        labels: {
            true: 'Yes',
            false: 'No'
        },
        default: false
    }
];

export const PAGE_HEADER_CONFIG: Omit<NICmsContentComponent.IMeta<NICmsContentComponent.IComponent>, 'type'> = {
    id: NPageHeader.BLOCK_ID,
    onDemandOnly: true,
    block: {
        url: NPageHeader.BLOCK_ID,
        scope: 'website',
        autoload: 'bootstrap'
    },
    name: 'Page Header',
    description: 'Page header component',
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        {
            key: 'logo_image',
            type: NICmsContentField.EFieldType.image,
            title: 'Logo Image',
            default: ''
        },
        {
            key: 'logo_alt',
            type: NICmsContentField.EFieldType.text,
            title: 'Logo ALT'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'menu',
            title: 'Menu Items',
            itemName: 'Item',
            itemNameField: 'text',
            complex: true,
            fields: [
                <NICmsContentField.ITextField>{
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Item text',
                    default: 'Some link',
                    required: true
                },
                <NICmsContentField.ITextField>{
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'color',
                    title: 'Color',
                    type: NICmsContentField.EFieldType.choice,
                    colorPreview: true,
                    options: ColorOptions,
                    description: 'Text color'
                },
                <NICmsContentField.IField>{
                    key: 'hide',
                    type: NICmsContentField.EFieldType.checkbox,
                    title: 'Hide',
                    default: false
                },
                <NICmsContentField.IField>{
                    key: 'components',
                    type: NICmsContentField.EFieldType.components,
                    title: 'Components',
                    itemName: 'component',
                    default: []
                },
                customerGroupVisibilityCmsField(CUSTOMER_GROUPS)
            ]
        },
        <NICmsContentField.IRepeaterField>{
            key: 'promobars',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Promobars',
            itemName: 'Promobar',
            defaultSize: 1,
            fields: [
                <NICmsContentField.IToggleField>{
                    key: 'turnOn',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Turn On',
                    default: false,
                    labels: {
                        true: 'Yes',
                        false: 'No'
                    }
                },
                { ...customerGroupVisibilityCmsField(CUSTOMER_GROUPS), key: 'visibility', title: 'Promobar visibility' },
                <NICmsContentField.IField>{
                    key: 'backgroundColor',
                    type: NICmsContentField.EFieldType.colorPicker,
                    title: 'Background Color',
                    default: '#333333'
                },
                <NICmsContentField.IField>{
                    key: 'fontColor',
                    type: NICmsContentField.EFieldType.colorPicker,
                    title: 'Font color',
                    default: 'white'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'paddingTop',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Padding Top',
                    options: PaddingTopOptions,
                    default: 'p-t-2'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'paddingBottom',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Padding Bottom',
                    options: PaddingBottomOptions,
                    default: 'p-b-2'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'paddingLeft',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Padding Left',
                    options: PaddingLeftOptions,
                    default: 'p-l-8'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'paddingRight',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Padding Right',
                    options: PaddingRightOptions,
                    default: 'p-r-8'
                },
                <NICmsContentField.IField>{
                    key: 'closeable',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Closeable',
                    description: 'Check if the promobar can be closed'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'animation',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Animation',
                    default: '',
                    options: [
                        ...UndefinedOption,
                        {
                            key: 'flowing_line',
                            title: 'Flowing line'
                        },
                        {
                            key: 'repeating_message',
                            title: 'Repeating message'
                        },
                        {
                            key: 'slider',
                            title: 'Slider'
                        }
                    ]
                },
                <NICmsContentField.IField>{
                    key: 'animationSpeed',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Animation Speed',
                    description: 'In seconds',
                    default: 20,
                    canShow: data => data?.animation === 'repeating_message' || data?.animation === 'flowing_line'
                },
                <NICmsContentField.IRepeaterField>{
                    key: 'promobarLines',
                    type: NICmsContentField.EFieldType.repeater,
                    title: 'Promobar Lines',
                    itemName: 'Line',

                    fields: [
                        <NICmsContentField.IRepeaterField>{
                            key: 'lineItems',
                            type: NICmsContentField.EFieldType.repeater,
                            title: 'Promobar Line Items',
                            itemName: 'Item',
                            itemNameField: 'text',
                            fields: [
                                <NICmsContentField.IChoiceField>{
                                    key: 'item_type',
                                    type: NICmsContentField.EFieldType.choice,
                                    title: 'Item type',
                                    default: 'text_or_link',
                                    options: [
                                        { key: 'none', title: '---' },
                                        { key: 'text_or_link', title: 'Text/Link' },
                                        { key: 'countdown', title: 'Countdown' },
                                        { key: 'separator', title: 'Separator' },
                                        { key: 'space', title: 'Additional space' }
                                    ]
                                },
                                <NICmsContentField.IField>{
                                    key: 'text',
                                    type: NICmsContentField.EFieldType.text,
                                    title: 'Text',
                                    canShow: data => data?.item_type === 'text_or_link'
                                },
                                <NICmsContentField.IField>{
                                    key: 'link',
                                    type: NICmsContentField.EFieldType.text,
                                    title: 'Link',
                                    canShow: data => data?.item_type === 'text_or_link'
                                },
                                <NICmsContentField.IChoiceField>{
                                    key: 'link_style',
                                    type: NICmsContentField.EFieldType.choice,
                                    title: 'Link Style',
                                    options: ButtonsOptions,
                                    canShow: data => data?.item_type === 'text_or_link'
                                },
                                <NICmsContentField.IToggleField>{
                                    key: 'underline',
                                    type: NICmsContentField.EFieldType.toggle,
                                    title: 'Underline',
                                    default: false,
                                    labels: {
                                        true: 'Yes',
                                        false: 'No'
                                    },
                                    canShow: data => data?.item_type === 'text_or_link'
                                },
                                <NICmsContentField.IToggleField>{
                                    key: 'bold',
                                    type: NICmsContentField.EFieldType.toggle,
                                    title: 'Bold',
                                    default: false,
                                    labels: {
                                        true: 'Yes',
                                        false: 'No'
                                    },
                                    canShow: data => data?.item_type === 'text_or_link'
                                },
                                <NICmsContentField.IDatetimeField>{
                                    key: 'end_date',
                                    type: NICmsContentField.EFieldType.datetime,
                                    title: 'End date',
                                    description: 'Format of the date: YYYY-MM-DD',
                                    canShow: data => data?.item_type === 'countdown'
                                }
                            ]
                        }
                    ],
                    maxSize: 2
                },
                <NICmsContentField.IToggleField>{
                    key: 'showSeparator',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Show separator',
                    description: 'Show separator dot between lines',
                    default: false,
                    labels: {
                        true: 'Yes',
                        false: 'No'
                    },
                    canShow: data => data?.animation === 'repeating_message'
                }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            key: 'search_redirects',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Search redirects',
            itemName: 'Redirect',
            itemNameField: 'from',
            fields: [
                {
                    key: 'from',
                    title: 'Search term string or regex',
                    description:
                        "Content is always matched as case insensitive regular expression. 'Red' matches 'red' and 'Red' but not 'red dress'. 'red.*' will match all of these.",
                    type: NICmsContentField.EFieldType.text,
                    required: true
                },
                {
                    key: 'to',
                    title: 'Redirect path',
                    type: NICmsContentField.EFieldType.text,
                    required: true
                }
            ]
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-wishlist-notice',
            title: 'Wishlist notice'
        },
        <NICmsContentField.IField>{
            key: 'wishlist_notice_added',
            type: NICmsContentField.EFieldType.text,
            title: "Notice 'added'",
            default: 'Item added to your wishlist.',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'wishlist_notice_already_on',
            type: NICmsContentField.EFieldType.text,
            title: "Notice 'already on'",
            default: 'The product is already in the wish list.',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'wishlist_notice_cta_label',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA label',
            default: 'View wishlist',
            required: true
        }
    ]
};
